import * as api from '@gitbook/api-client';
import {
    DEFAULT_HINT_DANGER_COLOR,
    DEFAULT_HINT_INFO_COLOR,
    DEFAULT_HINT_SUCCESS_COLOR,
    DEFAULT_HINT_WARNING_COLOR,
} from '@gitbook/colors';
import { Description, FieldGroup, Group, Label } from '@gitbook/spine-react';

import { useUpgradeSite } from '../../../state';
import { PaidFeatureIndicator } from '../../Site';
import type { SitesCustomizationPanelProps } from '../types';
import { setThemedColor } from '../utils';
import { ColorPickerForm } from './ColorPickerForm';

export const SEMANTIC_COLORS: Array<{
    colorType: Extract<
        keyof api.SiteCustomizationSettings['styling'],
        'infoColor' | 'successColor' | 'warningColor' | 'dangerColor'
    >;
    label: string;
}> = [
    {
        colorType: 'infoColor',
        label: 'Info',
    },
    {
        colorType: 'successColor',
        label: 'Success',
    },
    {
        colorType: 'warningColor',
        label: 'Warning',
    },
    {
        colorType: 'dangerColor',
        label: 'Danger',
    },
] as const;

// @site-semantic-colors - Keep it synced with backend by grepping that tag.

/**
 * Semantic colors settings for the site customization panel.
 */
export function SemanticColorSettings(
    props: SitesCustomizationPanelProps & { previewThemeMode: api.CustomizationThemeMode }
) {
    const { site, settings, setSettings, previewThemeMode } = props;

    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesAdvancedCustomization);

    const isFeatureActive = Object.entries({
        infoColor: DEFAULT_HINT_INFO_COLOR,
        successColor: DEFAULT_HINT_SUCCESS_COLOR,
        warningColor: DEFAULT_HINT_WARNING_COLOR,
        dangerColor: DEFAULT_HINT_DANGER_COLOR,
    } as const).some(
        ([key, defaultValue]) =>
            settings.styling[key].dark !== defaultValue ||
            settings.styling[key].light !== defaultValue
    );

    return (
        <>
            <FieldGroup orientation="horizontal">
                <Group>
                    <Label className="flex gap-2 align-items">
                        <span>Semantic colors</span>
                        {upgrade ? (
                            <PaidFeatureIndicator
                                upgrade={upgrade}
                                isFeatureActive={isFeatureActive}
                            />
                        ) : null}
                    </Label>
                    <Description>
                        Used to quickly convey meaning in elements across your site.
                    </Description>
                </Group>
            </FieldGroup>

            <div className="flex flex-row gap-4">
                {SEMANTIC_COLORS.map(({ colorType, label }) => (
                    <ColorPickerForm
                        {...props}
                        key={colorType}
                        label={label}
                        size="small"
                        orientation="vertical"
                        className="-ml-2 w-[6rem]"
                        currentColor={settings.styling[colorType][previewThemeMode]}
                        setColor={(newColor) => {
                            setSettings((prev) => ({
                                ...prev,
                                styling: {
                                    ...prev.styling,
                                    [colorType]: prev.styling
                                        ? setThemedColor(
                                              prev.styling[colorType],
                                              previewThemeMode,
                                              newColor
                                          )
                                        : undefined,
                                },
                            }));
                        }}
                    />
                ))}
            </div>
        </>
    );
}
